'use client';
import React, { Suspense, useContext, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { setCookie } from 'cookies-next';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { getCookie } from 'typescript-cookie';
import { getContentByTitle } from '@/components/customercenter-ui/components/product/details/helpers/utils/productDataUtils';
import ErrorIcon from '@/components/customercenter-ui/svgs/notifications/error';
import {
  showSoldAccountSection,
  hidePaymentGuardianSection,
} from '@/components/customercenter-ui/utils/assetSaleUtils';
import { CreditSummaryContext } from '@/context/creditSummaryProvider';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { useShowActiveBrandSection } from '@/frontastic/hooks/useShowActiveBrandSection';
import { syncGlassboxAccount } from '@/helpers/utils/glassbox';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { ErrorNotification } from '@/helpers/utils/notification';
import { PathNames, getPageUrl, isCreditSite, TranslateText } from '@/helpers/utils/siteUtils';
import CustomerAccountActions from './components/Actions/actions';
import CustomerCategoryMailingHistory from './components/CatalogMailingHistory/catalogMailingHistory';
import CustomerCreditHistory from './components/CreditHistory/creditHistory';
import CustomerCreditNotes from './components/CreditNotes/creditNotes';
import CustomerDetails from './components/Details/CustomerDetails';
import CustomerOrderHistory from './components/History/CustomerOrderHistory';
import CustomerNotes from './components/Notes/customerNotes';
import PaymentGuardian from './components/PaymentGuardian/paymentGuardian';
import CustomerRefundCheckHistory from './components/RefundCheckHistory/refundCheckHistory';
import CustomerShippingAddress from './components/ShippingAddress/shippingAddress';
import { PageTexts } from './i18n';
import { CustomerDetailsResponse, PaymentGuardianData } from './types';
import { transformCustomerDetailsData } from './utils';
import {
  BRAND_TEXT_TO_REPLACE,
  COOKIE_KEYS,
  CREDIT_NOTES,
  DEFAULT_SITE_LANG,
  RESPONSE_BAD_REQUEST,
  COOKIE_NAME_ZIP_CODE,
} from '../../../../constants/constant';
import BackButton from '../ui/back-button';
import Loader from '../ui/loader';
import { useToast } from '../ui/use-toast';

const CreditSummary = dynamic(() => import('./components/CreditSummary/creditSummary'), {
  ssr: false,
  loading: () => null,
});

const CustomerCartDetails = dynamic(() => import('./components/Cart/CartDetails'), {
  ssr: false,
  loading: () => null,
});
const CustomerDetailsPage = ({
  data,
  paymentGuardianData,
}: {
  data: CustomerDetailsResponse;
  paymentGuardianData: PaymentGuardianData;
}) => {
  const { toast } = useToast();
  const projectSettings: ProjectSettings = useProjSettings();
  const showActiveBrandSection = useShowActiveBrandSection();
  const { setCustomerData } = useContext(CreditSummaryContext);
  const [isSoldAccount, setIsSoldAccount] = useState(false);
  const [backUrl, setBackUrl] = useState('');
  const [siteId, setSiteId] = useState<string>();
  const [isCurrentSiteCreditSite, setIsCurrentSiteCreditSite] = useState<boolean>(false);
  const [showPaymentGuardianAccordian, setShowPaymentGuardianAccordian] = useState<boolean>(false);
  const [isClientSide, setIsClientSide] = useState(false);
  const [formattedPaymentGuardianData, setFormattedPaymentGuardianData] = useState<PaymentGuardianData>(
    {} as PaymentGuardianData,
  );
  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);
  const [currentSite, setCurrentSite] = useState('');
  const currentSiteName = currentSite && (mapLocaleToMeaningfulFormat() as { [key: string]: any })[currentSite];
  const debtProtectionEnabled =
    currentSite && (projectSettings?.debtProtectionEnabled?.[`${currentSite?.slice(0, 2)}`] ?? false);
  const { allowedPermissionsForAgent } = projectSettings;
  const allowedCreditSites = JSON.parse(projectSettings?.allowedCreditSites ?? 'ZBSite');

  useEffect(() => {
    const cookieSiteId = getCookie(COOKIE_KEYS.selectedSite) ?? 'ZBSite';
    setSiteId(cookieSiteId);
    setCurrentSite(cookieSiteId);
    setIsCurrentSiteCreditSite(isCreditSite());
  }, []);

  useEffect(() => {
    if (currentSiteName) {
      getContentByTitle('learnMore').then((data: any) => {
        setFormattedPaymentGuardianData((prev) => ({
          ...prev,
          learnMore:
            data?.htmlContentEntries?.[0]?.rich_text_and_html_editor?.replaceAll(
              BRAND_TEXT_TO_REPLACE,
              currentSiteName,
            ) || '',
        }));
      });

      getContentByTitle('paymentGuardianAgreement').then((data: any) => {
        setFormattedPaymentGuardianData((prev) => ({
          ...prev,
          paymentGuardianAgreement:
            data?.htmlContentEntries?.[0]?.rich_text_and_html_editor?.replaceAll(
              BRAND_TEXT_TO_REPLACE,
              currentSiteName,
            ) || '',
        }));
      });
    }
  }, [currentSiteName]);

  useEffect(() => {
    if (currentSiteName) {
      setFormattedPaymentGuardianData({
        benefitsDirect: paymentGuardianData.benefitsDirect?.replaceAll(BRAND_TEXT_TO_REPLACE, currentSiteName),
        customerCare: paymentGuardianData.customerCare?.replaceAll(BRAND_TEXT_TO_REPLACE, currentSiteName),
        paymentGuardianSuccessMessage: paymentGuardianData.paymentGuardianSuccessMessage?.replaceAll(
          BRAND_TEXT_TO_REPLACE,
          currentSiteName,
        ),
        paymentGuardianErrorMessage: paymentGuardianData?.paymentGuardianErrorMessage,
      });
    }
  }, [paymentGuardianData, currentSiteName]);

  const {
    accountAddress,
    accountData,
    emailRecords,
    phoneRecords,
    customer,
    isEligible,
    divisionCustomerId,
    billingAddress,
    creditAccountId,
    primaryEmail,
    creditProtectionInfo,
    customerId,
    omsCustomerId,
    divisionCode,
    billingZipCode,
  } = transformCustomerDetailsData(data);

  useEffect(() => {
    const isSoldAccount = customer?.DivisionCustomers?.[0]?.CreditAccount?.AccountInfo?.isSoldAccount ?? false;
    setIsSoldAccount(isSoldAccount);
  }, [customer]);

  useEffect(() => {
    if (customerId) {
      const currentUrlData = {
        customerId: customerId,
        omsCustomerId: omsCustomerId,
        divisionCode: divisionCode,
        idName: 'customerId',
      };
      setCookie(COOKIE_KEYS.CDP_URL, JSON.stringify(currentUrlData), {
        sameSite: 'none',
        secure: true,
        partitioned: true,
      });
      setCookie(COOKIE_NAME_ZIP_CODE, billingZipCode, {
        sameSite: 'none',
        secure: true,
        partitioned: true,
      });
      setCustomerData(data?.customerDetails?.Customer);
      syncGlassboxAccount(customerId || omsCustomerId);
      const customerEmail =
        data?.customerDetails?.Customer?.DivisionCustomers?.[0]?.EmailRecords?.[0]?.EmailInfo?.emailAddress ?? '';
      const sessionEmail = sessionStorage.getItem('customerEmail');
      if (sessionEmail) {
        sessionStorage.removeItem('customerEmail');
      }
      sessionStorage.setItem('customerEmail', customerEmail);
    }
  }, [customerId]);

  useEffect(() => {
    const backurl = getPageUrl(PathNames.CUSTOMER_SEARCH);
    setBackUrl(backurl);
  }, []);

  useEffect(() => {
    setIsClientSide(true);
  }, []);

  useEffect(() => {
    if (isClientSide) {
      if (data?.customerDetails?.code === RESPONSE_BAD_REQUEST) {
        ErrorNotification(toast, {
          description: data?.customerDetails?.message,
          action: <ErrorIcon className="h-5 w-5" />,
        });
      } else if (data?.orderHistory?.code === RESPONSE_BAD_REQUEST) {
        ErrorNotification(toast, {
          description: data?.orderHistory?.message,
          action: <ErrorIcon className="h-5 w-5" />,
        });
      }
    }
  }, [isClientSide, data]);

  useEffect(() => {
    if (currentSiteName && debtProtectionEnabled && formattedPaymentGuardianData) {
      if (formattedPaymentGuardianData?.learnMore && formattedPaymentGuardianData?.paymentGuardianAgreement) {
        setShowPaymentGuardianAccordian(true);
      }
    }
  }, [currentSiteName, debtProtectionEnabled, formattedPaymentGuardianData]);

  return (
    <section className="px-6 py-5">
      <div className="flex h-full items-center justify-between">
        <p className="pb-5 text-xl font-bold">{Texts.customerDetails}</p>
        <BackButton text={Texts.backToCustomerSearch} className={`flex text-blueLight underline`} url={backUrl} />
        {Texts.catalogDescription}
      </div>
      <section className="flex flex-wrap gap-4 lg:flex-nowrap">
        <section className="w-full lg:w-4/5">
          <div className="flex flex-col gap-4">
            <CustomerDetails
              CustomerAccountAddress={accountAddress}
              CustomerAccountData={accountData}
              Texts={Texts}
              emailRecords={emailRecords}
              phoneRecords={phoneRecords}
              creditAccountId={creditAccountId}
              customer={customer}
            />
            <CustomerNotes Texts={Texts} />
            <CustomerOrderHistory Texts={Texts} billingZipCode={billingZipCode} />
            {showActiveBrandSection &&
            !showSoldAccountSection(projectSettings, isSoldAccount) &&
            isCurrentSiteCreditSite &&
            isClientSide ? (
              <CustomerCreditHistory
                primaryEmail={primaryEmail}
                customerDetails={customer}
                billingAddress={billingAddress}
                creditAccountId={creditAccountId}
                customerId={customerId}
                Texts={Texts}
              />
            ) : (
              <></>
            )}
            {showActiveBrandSection &&
              !showSoldAccountSection(projectSettings, isSoldAccount) &&
              allowedPermissionsForAgent?.includes(CREDIT_NOTES) &&
              isClientSide &&
              isCurrentSiteCreditSite && <CustomerCreditNotes Texts={Texts} creditAccountId={creditAccountId} />}
            <CustomerRefundCheckHistory Texts={Texts} />
            <CustomerShippingAddress Texts={Texts} />
            {isClientSide && (isCurrentSiteCreditSite || currentSite === 'MQSite') ? (
              <CustomerCategoryMailingHistory
                divisionCustomerId={divisionCustomerId}
                billingAddress={billingAddress}
                customerDetails={customer}
              />
            ) : (
              <></>
            )}
            {showActiveBrandSection &&
              showPaymentGuardianAccordian &&
              !hidePaymentGuardianSection(projectSettings, isSoldAccount) && (
                <PaymentGuardian
                  creditProtectionInfo={creditProtectionInfo}
                  paymentGuardianData={formattedPaymentGuardianData}
                  isEligible={isEligible}
                />
              )}
          </div>
        </section>
        <section className="w-full lg:max-w-[335px]">
          <CustomerAccountActions
            Texts={Texts}
            customerDetails={customer}
            primaryEmail={primaryEmail}
            creditAccountId={customerId}
            billingAddress={billingAddress}
          />
          <div className="flex flex-col gap-4">
            <Suspense
              fallback={
                <div className="flex items-center justify-center">
                  <Loader />
                </div>
              }
            >
              <CustomerCartDetails />
            </Suspense>
            <Suspense
              fallback={
                <div className="flex items-center justify-center">
                  <Loader />
                </div>
              }
            >
              {allowedCreditSites?.creditSites?.includes(siteId) && <CreditSummary />}
            </Suspense>
          </div>
        </section>
      </section>
    </section>
  );
};

export default CustomerDetailsPage;
